import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import {
  PlayCircle,
  X,
  CalendarPlus,
  Sparkles,
  Users,
  Gift,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { demoStepsFor, hasSelfHostedDemo } from '../lib/demoWalkthrough'

const DISMISS_KEY = 'storyfy_tour_dismissed'

// Published Guidejar walkthroughs, one per language. Set these in .env once the
// recordings are published; if a language has no URL we fall back to the
// built-in slide deck below, so the tour always works.
const GUIDEJAR_URL: Record<string, string | undefined> = {
  en: process.env.REACT_APP_GUIDEJAR_DEMO_URL_EN,
  ka: process.env.REACT_APP_GUIDEJAR_DEMO_URL_KA,
}

// Reject anything that isn't an https guidejar.com URL — catches typos, http,
// and pasted junk. It deliberately does NOT promise the host is reachable: a
// non-existent subdomain still parses fine, and no client-side check can tell
// a live host from a dead one. That's what the in-modal escape hatch is for.
// Copy the real URL from Guidejar's own Share/Embed dialog on a published guide.
const embedSrc = (raw?: string): string | undefined => {
  if (!raw) return undefined
  try {
    const url = new URL(raw)
    const host = url.hostname.toLowerCase()
    const ok =
      url.protocol === 'https:' &&
      (host === 'guidejar.com' || host.endsWith('.guidejar.com'))
    return ok ? url.toString() : undefined
  } catch {
    return undefined
  }
}

// Dismissible first-run onboarding banner + an in-app walkthrough modal.
const OnboardingTour = () => {
  const { language } = useLanguage()
  const navigate = useNavigate()
  const ka = language === 'ka'
  const configuredEmbed = embedSrc(GUIDEJAR_URL[ka ? 'ka' : 'en'])
  // Lets the viewer bail out of a broken embed. The recording is hosted by a
  // third party, so "the URL is valid" never means "it loaded".
  const [embedRejected, setEmbedRejected] = useState(false)
  const embedUrl = embedRejected ? undefined : configuredEmbed
  // Tier 2: our own copy of the walkthrough, served from this origin. Shown
  // when there is no usable embed but we do have exported step images.
  const selfHosted = hasSelfHostedDemo(language)
  const shots = demoStepsFor(language)
  const showSelfHosted = !embedUrl && selfHosted
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)
  // Separate cursor from `step` so the two decks can't desync when the tour
  // falls from one tier to another mid-view.
  const [shotIndex, setShotIndex] = useState(0)

  // The walkthrough steps. Step 2 highlights the new AI invitation designer.
  const STEPS = [
    {
      icon: CalendarPlus,
      title: ka ? 'შექმენი ღონისძიება' : 'Create an event',
      body: ka
        ? 'დაიწყე ძირითადით — სათაური, თარიღი და ადგილმდებარეობა. რამდენიმე წუთში მზად ხარ.'
        : 'Start with the basics — title, date, and location. You’ll be ready in a couple of minutes.',
    },
    {
      icon: Sparkles,
      title: ka ? 'გააფორმე მოწვევა' : 'Design the invitation',
      body: ka
        ? 'აირჩიე თემა ან მიეცი AI-ს, შექმნას ფონი და შესაბამისი შრიფტები შენი ღონისძიებისთვის.'
        : 'Pick a theme — or let AI generate a custom background and matching fonts for your event.',
    },
    {
      icon: Users,
      title: ka ? 'მოიწვიე და თვალი ადევნე RSVP-ებს' : 'Invite & track RSVPs',
      body: ka
        ? 'გააზიარე ბმული და ნახე ვინ მოდის რეალურ დროში — დადასტურებები, „იქნებ“ და უარები.'
        : 'Share the link and see who’s coming in real time — confirmations, maybes, and declines.',
    },
    {
      icon: Gift,
      title: ka ? 'შეაგროვე საჩუქრები' : 'Collect gifts',
      body: ka
        ? 'დაამატე სასურველი საჩუქრების სია და მიიღე შემოწირულობები პირდაპირ ლარებში.'
        : 'Add a wishlist and receive contributions directly — in GEL.',
    },
  ]

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
    setDismissed(true)
  }

  const startTour = () => {
    setStep(0)
    setShotIndex(0)
    // Give the embed another chance each time the tour is opened — a failure
    // may have been a transient network blip, not a bad URL.
    setEmbedRejected(false)
    setOpen(true)
  }

  const isLast = step === STEPS.length - 1
  const next = () => {
    if (isLast) {
      setOpen(false)
      navigate('/events/create')
      return
    }
    setStep((s) => s + 1)
  }
  const back = () => setStep((s) => Math.max(0, s - 1))

  const current = STEPS[step]
  const Icon = current.icon

  // Self-hosted deck: clamp the cursor so a shrinking step list can never index
  // past the end, and default the callout anchor to a safe lower-left spot.
  const activeShot = shots[Math.min(shotIndex, Math.max(shots.length - 1, 0))]
  const hotspot = activeShot?.hotspot ?? { x: 22, y: 58 }

  return (
    <>
      {!dismissed && (
        <div
          className="relative mb-6 overflow-hidden rounded-bubble border border-celebration-violet/25 bg-celebration-violet/[0.08] p-4 sm:p-5 animate-fade-in dark:border-celebration-violet/30 dark:bg-celebration-violet/15"
          data-testid="onboarding-tour"
        >
          <div className="flex items-center gap-4 pr-8">
            <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-celebration-violet/15 sm:flex">
              <PlayCircle className="h-6 w-6 text-celebration-grape dark:text-[#c4b5fd]" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-celebration-ink dark:text-[#f4f0ff]">
                {ka ? 'ახალი ხარ Storyfy-ზე?' : 'New to Storyfy?'}
              </p>
              <p className="text-sm text-celebration-ink-2 dark:text-[#c3b8dd]">
                {ka
                  ? 'უყურე მოკლე გაცნობას: ღონისძიების შექმნა, მოწვევის გაფორმება და სტუმრების მართვა.'
                  : 'Take a quick walkthrough — create an event, customize the invite, and manage guests.'}
              </p>
            </div>
            <div className="shrink-0">
              <Button
                type="button"
                onClick={startTour}
                className="btn-gradient rounded-full px-4 text-white"
                data-testid="start-tour-btn"
              >
                <PlayCircle className="mr-2 h-4 w-4" />
                {ka ? 'ტურის ნახვა' : 'Watch the tour'}
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={ka ? 'დახურვა' : 'Dismiss'}
            className="absolute right-3 top-3 rounded-full p-1 text-celebration-ink-2 transition-colors hover:bg-black/5 dark:text-[#c3b8dd] dark:hover:bg-white/10"
            data-testid="dismiss-tour-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        {embedUrl ? (
          // Published Guidejar walkthrough for the current language.
          <DialogContent
            className="max-w-4xl gap-0 rounded-bubble p-0 overflow-hidden"
            data-testid="tour-modal-embed"
          >
            <DialogTitle className="sr-only">
              {ka ? 'ღონისძიების შექმნის გაცნობა' : 'Create an event — walkthrough'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {ka
                ? 'ინტერაქტიული გაცნობა: როგორ შექმნა ღონისძიება Storyfy-ზე.'
                : 'Interactive walkthrough of creating an event on Storyfy.'}
            </DialogDescription>
            <div className="aspect-video w-full bg-black">
              <iframe
                src={embedUrl}
                title={ka ? 'Storyfy — ღონისძიების შექმნა' : 'Storyfy — create an event'}
                className="h-full w-full border-0"
                allow="fullscreen"
              />
            </div>
            <div className="flex items-center justify-between gap-3 px-5 py-3">
              <p className="text-xs text-celebration-ink-2 dark:text-[#c3b8dd]">
                {ka ? 'ვიდეო არ ჩაიტვირთა?' : "Walkthrough not loading?"}
              </p>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEmbedRejected(true)}
                className="rounded-full text-sm text-celebration-ink-2 dark:text-[#c3b8dd]"
                data-testid="tour-embed-fallback-btn"
              >
                {ka ? 'ნახე მოკლე ნაბიჯები' : 'Show the quick steps'}
              </Button>
            </div>
          </DialogContent>
        ) : showSelfHosted ? (
          // Tier 2: our own exported copy of the walkthrough. Same journey,
          // served from this origin, so it works with Guidejar unreachable.
          <DialogContent
            className="max-h-[92vh] max-w-3xl gap-0 overflow-y-auto rounded-bubble p-0"
            data-testid="tour-modal-selfhosted"
          >
            <DialogTitle className="sr-only">
              {ka ? 'ღონისძიების შექმნის გაცნობა' : 'Create an event — walkthrough'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {ka
                ? 'ნაბიჯ-ნაბიჯ გაცნობა: როგორ შექმნა ღონისძიება Storyfy-ზე.'
                : 'Step-by-step walkthrough of creating an event on Storyfy.'}
            </DialogDescription>

            {/* Browser-chrome frame, mirroring how a captured walkthrough is
                normally presented: the shot reads as a real window rather than
                a bare screenshot pasted into a dialog. */}
            <div className="bg-grad-juicy p-3 sm:p-5">
              <div className="overflow-hidden rounded-xl bg-[#1b1033] shadow-2xl ring-1 ring-black/20">
                {/* Title bar */}
                <div className="flex items-center gap-2 bg-[#241748] px-3 py-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
                  <div className="ml-2 flex-1 truncate rounded-md bg-black/25 px-2.5 py-1 text-[11px] text-white/50">
                    storyfy.ge
                  </div>
                  <span className="text-[11px] font-medium tabular-nums text-white/45">
                    {shotIndex + 1}/{shots.length}
                  </span>
                </div>

                {/* Shot + callout. Not lazy: this modal only mounts once opened,
                    so the image is above the fold by definition. Deferring it
                    delays the one thing the viewer came for — and environments
                    that suspend rendering never trigger the load at all. */}
                <div className="relative">
                  {/* Height-capped so a tall shot can't push the callout and
                      caption off-screen; object-contain keeps the aspect ratio
                      and the hotspot percentages stay relative to this box. */}
                  <img
                    key={activeShot.image}
                    src={activeShot.image}
                    alt={activeShot.title}
                    className="block max-h-[52vh] w-full animate-fade-in bg-black/40 object-contain"
                  />

                  {/* Pulsing hotspot on the element in question. */}
                  <span
                    className="pointer-events-none absolute z-10 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                    style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
                    aria-hidden="true"
                  >
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-celebration-pink/60" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-celebration-pink ring-2 ring-white/80" />
                  </span>

                  {/* The callout, anchored near the hotspot and clamped so it
                      can never hang off the edge of the frame. */}
                  <div
                    key={shotIndex}
                    className="absolute z-20 w-[min(19rem,72%)] animate-control-pop rounded-xl bg-[#ffe66d] p-3.5 shadow-xl ring-1 ring-black/10"
                    style={{
                      left: `${Math.min(Math.max(hotspot.x, 4), 62)}%`,
                      top: `${Math.min(Math.max(hotspot.y + 6, 4), 68)}%`,
                    }}
                  >
                    <p className="text-[13px] font-medium leading-snug text-[#1b1033]">
                      {activeShot.body}
                    </p>
                    <div className="mt-2.5 flex items-center justify-between gap-3">
                      <span className="text-[11px] font-semibold tabular-nums text-[#1b1033]/55">
                        {shotIndex + 1}/{shots.length}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setShotIndex((i) => Math.max(0, i - 1))}
                          disabled={shotIndex === 0}
                          aria-label={ka ? 'უკან' : 'Back'}
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1b1033]/10 text-[#1b1033] transition-colors hover:bg-[#1b1033]/20 disabled:opacity-0"
                          data-testid="tour-shot-back-btn"
                        >
                          <ArrowLeft className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (shotIndex === shots.length - 1) {
                              setOpen(false)
                              navigate('/events/create')
                              return
                            }
                            setShotIndex((i) => i + 1)
                          }}
                          aria-label={
                            shotIndex === shots.length - 1
                              ? ka
                                ? 'შექმენი ღონისძიება'
                                : 'Create your first event'
                              : ka
                                ? 'შემდეგი'
                                : 'Next'
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1b1033] text-white shadow-sm transition-transform hover:scale-105"
                          data-testid="tour-shot-next-btn"
                        >
                          {shotIndex === shots.length - 1 ? (
                            <CalendarPlus className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowRight className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Caption strip + progress, outside the frame so it doesn't fight
                the screenshot for attention. */}
            <div className="px-6 pb-5 pt-4">
              <p className="font-display text-lg font-semibold text-celebration-ink dark:text-[#f4f0ff]">
                {activeShot.title}
              </p>

              <div className="mt-3 flex items-center justify-center gap-1.5">
                {shots.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setShotIndex(i)}
                    aria-label={`${ka ? 'ნაბიჯი' : 'Step'} ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === shotIndex ? 'w-5 bg-celebration-pink' : 'w-1.5 bg-celebration-ink/15 dark:bg-white/20'
                    }`}
                  />
                ))}
              </div>

              <div className="mt-4 flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setOpen(false)
                    navigate('/events/create')
                  }}
                  className="rounded-full text-sm text-celebration-ink-2 dark:text-[#c3b8dd]"
                  data-testid="tour-shot-skip-btn"
                >
                  {ka ? 'გამოტოვე და დაიწყე' : 'Skip and start'}
                </Button>
              </div>
            </div>
          </DialogContent>
        ) : (
        <DialogContent className="max-w-md gap-0 rounded-bubble p-0 overflow-hidden" data-testid="tour-modal">
          {/* Illustrated header */}
          <div className="bg-grad-juicy px-6 pb-8 pt-7 text-white">
            <div
              key={step}
              className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 animate-control-pop"
            >
              <Icon className="h-7 w-7" />
            </div>
            <DialogTitle className="mt-4 font-display text-2xl font-semibold leading-tight">
              {current.title}
            </DialogTitle>
            <p className="mt-1 text-sm text-white/85">
              {ka ? `ნაბიჯი ${step + 1} / ${STEPS.length}` : `Step ${step + 1} of ${STEPS.length}`}
            </p>
          </div>

          <div className="px-6 pb-6 pt-5">
            <DialogDescription
              key={step}
              className="min-h-[3.5rem] text-[15px] leading-relaxed text-celebration-ink-2 dark:text-[#c3b8dd] animate-fade-in"
            >
              {current.body}
            </DialogDescription>

            {/* Progress dots */}
            <div className="mt-5 flex items-center justify-center gap-1.5">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === step ? 'w-5 bg-celebration-pink' : 'w-1.5 bg-celebration-ink/15 dark:bg-white/20'
                  }`}
                />
              ))}
            </div>

            {/* Controls */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={back}
                disabled={step === 0}
                className="rounded-full text-celebration-ink-2 disabled:opacity-0 dark:text-[#c3b8dd]"
                data-testid="tour-back-btn"
              >
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                {ka ? 'უკან' : 'Back'}
              </Button>
              <Button
                type="button"
                onClick={next}
                className="btn-gradient rounded-full px-5 font-semibold text-white"
                data-testid="tour-next-btn"
              >
                {isLast ? (
                  <>
                    <CalendarPlus className="mr-1.5 h-4 w-4" />
                    {ka ? 'შექმენი ღონისძიება' : 'Create your first event'}
                  </>
                ) : (
                  <>
                    {ka ? 'შემდეგი' : 'Next'}
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
        )}
      </Dialog>
    </>
  )
}

export default OnboardingTour
